import { useEffect, useMemo, useState } from 'react';
import useELT from 'new/components/flows/types/elt/CreateELTFlow/ELTFlow/useELT';
import { useForm } from 'new/elements/Form/useForm';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import { GET_DATA_SOURCE_REQUEST, GET_SINK_REQUEST } from 'js/constants/actionTypes';
import useRequests from 'new/state/request/repository/useRequests';
import useFlowQueryParams from 'new/components/flows/useFlowQueryParams';
import useFlowParams from 'new/components/flows/useFlowParams';
import usePaging from 'new/state/paging/repository/usePaging';
import createFlowService from 'new/components/flows/service/createFlowService';
import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';

export default function useInitialSetupPage() {
  const { resourceType, id, nexsetId } = useFlowParams();
  const [initialLoading, setInitialLoading] = useState(true);
  const { flowId } = useFlowQueryParams();
  const { updateFormData, selectedConnection, credentialId, connectionType, source, nexset, sink } =
    useELT();
  const { showError } = useRequests();
  const { setLoaderMessages } = useForm();
  const { requestResources } = useFlowsAdapter();
  const { requestNexset } = useNexsetsAdapter();
  const { getConnectionCategoryByConnectorType } = usePaging();
  const flowService = createFlowService();

  useEffect(() => {
    setLoaderMessages({
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [GET_SINK_REQUEST]: 'Loading destination',
    });
  }, []);

  useEffect(() => {
    updateFormData({
      connectionType: !!flowId ? 'SINK' : 'SOURCE',
      flowType: 'elt',
    });
  }, []);

  const setup = async () => {
    setInitialLoading(true);
    let sink, source, nexset;

    try {
      // First, try to load resources via requestResources
      ({ sink, source, nexset } = await requestResources({
        resourceType,
        id,
        flowId,
        nexsetId,
      }));

      // If nexset wasn't loaded but nexsetId exists, load it directly
      // This ensures nexset is always available for sink creation
      if (!nexset && nexsetId) {
        nexset = await requestNexset(nexsetId);
      }

      const _selectedConnection =
        resourceType === 'sink'
          ? sink?.connector_type || selectedConnection
          : source?.connector_type || selectedConnection;

      // For FILE-category sinks, the FileSystem/SelectBucket form reads field
      // values from `sinkConfig` (and the selected folder from `selectedBucket`)
      // in form state, not from the sink entity. ELT was never seeding either
      // on edit, so iceberg fields fell back to spec defaults and the folder
      // picker rendered blank for regular S3/GCS/etc. Mirror what CDC does.
      const sinkConnectionCategory = getConnectionCategoryByConnectorType(_selectedConnection);
      let initialSinkConfig: any = undefined;
      let initialSelectedBucket: any = undefined;
      if (sink && sinkConnectionCategory === CONNECTION_CATEGORY_FILE) {
        const { config, selectedBucket } = flowService.initializeFormToFileCategory(
          sink?.sink_config,
        );
        initialSinkConfig = {
          name: sink?.name,
          description: sink?.description,
          ...(config || {}),
        };
        initialSelectedBucket = selectedBucket;
      }

      updateFormData({
        source,
        nexset,
        sink,
        resourceConfig: {
          resourceConfig: {},
        },
        ...(initialSinkConfig ? { sinkConfig: initialSinkConfig } : {}),
        ...(initialSelectedBucket ? { selectedBucket: initialSelectedBucket } : {}),
        selectedConnection: _selectedConnection,
        ...(sink || source
          ? {
              // Prefer the user's current selection (set on the authenticate
              // page) over the resource's original credential — otherwise going
              // back to change the credential and returning would silently
              // revert it on re-mount.
              credentialId:
                resourceType === 'sink'
                  ? credentialId || sink?.data_credentials
                  : credentialId || source?.data_credentials,
            }
          : {}),
      });
    } catch (e) {
      showError('There was an error trying to setup the resources');
    }

    setInitialLoading(false);
  };

  useEffect(() => {
    updateFormData({
      connectionType: resourceType.toUpperCase(),
    });
  }, []);

  useEffect(() => {
    setup();
  }, []);

  return initialLoading;
}
